/**
 * Supabase Client Configuration
 *
 * This file initializes the Supabase client for the Click&Share v2 project.
 * The client is configured to work with Sandpack's environment variable system.
 */

import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';
import i18n from './i18n';

// IMPORTANT: For Vite deployments (Vercel), use import.meta.env
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Check your .env file.');
}

// Create the Supabase client with TypeScript support
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

/**
 * Check if an error is a JWT/auth expiry error
 */
function isAuthError(error: any): boolean {
  if (!error) return false;
  const message = (error.message || error.msg || '').toLowerCase();
  const code = error.code || '';
  const status = error.status || error.statusCode || 0;
  return (
    status === 401 ||
    code === 'PGRST301' ||
    code === '401' ||
    message.includes('jwt expired') ||
    message.includes('jwt') && message.includes('expired') ||
    message.includes('invalid token') ||
    message.includes('token is expired') ||
    message.includes('not authenticated')
  );
}

/**
 * Check if an error is a Postgres/RLS permission error.
 * Postgres returns SQLSTATE 42501 for "insufficient_privilege", which is what
 * a failed row-level security policy surfaces as (e.g. "new row violates
 * row-level security policy").
 */
function isPermissionError(error: any): boolean {
  if (!error) return false;
  const message = (error.message || error.msg || '').toLowerCase();
  const code = error.code || '';
  return (
    code === '42501' ||
    message.includes('row-level security') ||
    message.includes('row level security')
  );
}

/**
 * Helper function to handle Supabase errors.
 * If the error is JWT/auth related, triggers a background session refresh
 * so the next user action will use a fresh token.
 */
export function handleSupabaseError(error: any): string {
  if (!error) return 'An unknown error occurred';

  // Surface a friendly, translated message for permission/RLS failures
  // instead of leaking the raw Postgres error to the user.
  if (isPermissionError(error)) {
    return i18n.t('errors.permissionDenied');
  }

  // Detect JWT expiry and attempt background refresh
  if (isAuthError(error)) {
    console.warn('[Supabase] Auth error detected, attempting session refresh...', error.message || error);
    supabase.auth.refreshSession().then(({ data, error: refreshError }) => {
      if (refreshError) {
        console.error('[Supabase] Session refresh failed — user may need to re-login:', refreshError.message);
      } else if (data.session) {
        console.log('[Supabase] Session refreshed successfully');
      }
    });
  }

  if (typeof error === 'string') return error;

  if (error.message) return error.message;

  return 'An unexpected error occurred';
}

/**
 * Helper function to check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  const { data: { session } } = await supabase.auth.getSession();
  return !!session;
}

/**
 * Helper function to get current user
 */
export async function getCurrentUser() {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
}

/**
 * Get user profile with merchant access info
 * Returns profile including merchant_id and is_platform_admin flag
 */
export async function getUserProfile(userId: string) {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }

  return data;
}

/**
 * Get user roles from user_merchant_roles table
 * Returns array of roles with merchant_id, location_id, and role level
 */
export async function getUserRoles(userId: string) {
  const { data, error } = await supabase
    .from('user_merchant_roles')
    .select('*')
    .eq('user_id', userId);

  if (error) {
    console.error('Error fetching user roles:', error);
    return [];
  }

  return data || [];
}

/**
 * Check if current user is platform admin
 */
export async function isPlatformAdmin(userId: string): Promise<boolean> {
  const profile = await getUserProfile(userId);
  return profile?.is_platform_admin ?? false;
}

/**
 * Get merchant ID for current user
 */
export async function getUserMerchantId(userId: string): Promise<string | null> {
  const profile = await getUserProfile(userId);
  return profile?.merchant_id ?? null;
}

/**
 * Get location ID for current user (if location-specific)
 * Returns the location_id from the user's first role, or null if merchant-wide
 */
export async function getUserLocationId(userId: string): Promise<string | null> {
  const roles = await getUserRoles(userId);
  if (roles.length === 0) return null;
  return roles[0].location_id ?? null;
}
